import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import Flex from "./Flex";
import SideSheetDrawer from "./SidesheetDrawer";
// import { useSpecialtiesList } from "../../../../../queries/use-specialities-list";
import { useGetPatient } from "@/queries/use-get-patient-uuid";
import { cloneDeep } from "lodash";
import { INITIAL_PATIENT_INSURANCE } from "../constants/provider/patient/add-new-patient/add-new-patient-form";
import { useSnackbar } from "../hooks/useSnackbar";
import { useCreatePatientInsurance } from "../queries/insurance/use-create-patient-insurance";
import { useUpdatePatientInsurance } from "../queries/insurance/use-update-patient-insurance";
import { PatientInsurance } from "../services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
} from "../utils/utils";
import Insurance from "./app/provider/patient/add-new-patient/Insurance";

interface NewPatientInsuranceProps {
  opened: boolean;
  onClose: () => void;
  patientId?: string;
}

type InsuranceForm = {
  patientInsurances: PatientInsurance &
    {
      planType?: any;
      planName?: any;
      planId?: any;
      insurancePayer?: any;
      provider?: { [key: string]: string };
    }[];
};
// note : this is a temporary formik form for insurance, it will be replaced with the modal
const NewPatientInsuranceDrawer = ({
  opened,
  onClose,
  patientId,
}: NewPatientInsuranceProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const isEditInsurance = !patientId;
  patientId = patientId ?? "";
  const { data: patientData } = useGetPatient(patientId, !!patientId && opened);
  const { mutateAsync: updatePatientInsurance } = useUpdatePatientInsurance();
  const { mutateAsync: createPatientInsurance } = useCreatePatientInsurance();
  const formik = useFormik<InsuranceForm>({
    initialValues: {
      patientInsurances: [INITIAL_PATIENT_INSURANCE],
    },
    // Effective Start Date is restricted at selection time via the date picker's
    // minDate (set to Patient DOB) in the Insurance form, so no Yup validation
    // is needed here.
    onSubmit,
    enableReinitialize: true,
  });

  function onSubmit(values: InsuranceForm) {
    const copiedValues: any = cloneDeep(values);
    copiedValues.patientInsurances = values.patientInsurances?.map((it) => ({
      ...it,
      patient: {
        [patientId ?? ""]: "",
      },
      provider: it?.provider
        ? {
            [it?.provider.value]: `${it.provider.label}`,
          }
        : undefined,
      insurancePayer: {
        ...it.insurancePayer,
        payerId: it?.insurancePayer?.payerId?.value,
      },
      planId: it?.planId?.value,
      planName: it?.planId?.label,
      planType: it?.planType?.trim()?.length > 0 ? it?.planType : undefined,
    }));

    const transFormData: any = getUnNecessaryTransformData(
      ["languages"],
      copiedValues
    );

    const payload = {
      patientInsurance: isEditInsurance
        ? transFormData.patientInsurances[0]
        : (transFormData.patientInsurances as any),
      xTENANTID: getXtenantId() ?? undefined,
    };

    showToastAndReset(
      isEditInsurance
        ? updatePatientInsurance(payload as any)
        : createPatientInsurance(payload as any)
    );
  }

  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({
          queryKey: ["appointment-list"],
        });
        closeAndReset();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };
  const closeAndReset = () => {
    formik.resetForm();
    onClose();
    // onClose("appointment-list");
  };

  return (
    <SideSheetDrawer
      open={opened}
      title="Add Insurance"
      w="50vw"
      onClose={closeAndReset}
      footerActions={
        <>
          <Button
            size="medium"
            variant="outlined"
            onClick={() => closeAndReset()}
            sx={{ lineHeight: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            size="medium"
            variant="contained"
            onClick={() => formik.handleSubmit()}
            sx={{ lineHeight: 1.5 }}
          >
            Save
          </Button>
        </>
      }
    >
      <Flex width="100%" flexDirection={"column"}>
        <Insurance
          formik={formik as any}
          needSectionLabel={false}
          addOneInsurance={false}
          patientData={patientData}
        />
      </Flex>
    </SideSheetDrawer>
  );
};

export default NewPatientInsuranceDrawer;
